import { useMutation, useReactiveVar } from '@apollo/client';
import {
  IconButton,
  Stack,
  Text,
  TooltipHost,
  makeStyles,
} from '@fluentui/react';
import clsx from 'clsx';
import { ActionMessageModal } from 'common/components/ActionMessageModal';
import { ApprovalFlowModal } from 'common/components/ApprovalFlowModal';
import { CloseButton } from 'common/components/Buttons';
import { showCustomToast } from 'common/components/CustomToast/utils';
import { StatusIcon } from 'common/components/StatusIcon';
import { UnsavedIndicator } from 'common/components/UnsavedIndicator';
import { DRAWER_CLOSE_WIDTH, DRAWER_OPEN_WIDTH } from 'common/constants';
import { useCommonStyles } from 'common/styles';
import { EntityType } from 'common/types/utility';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Approvals_approvals } from 'settings/account/approvalSetup/list/__generated__/Approvals';
import { expandedNav } from 'utility/cache/ui';
import DELETE_APPROVAL from '../ApprovalFlowDelete.graphql';
import { ApprovalSetupFlowCommonData_Query } from '../../__generated__/ApprovalSetupFlowCommonData';
import { FlowApproval } from '../../__generated__/FlowApproval';
import { ActionsMenu } from './ActionMenu';
import { CopyApproval } from './CopyApproval';
import {
  ApprovalFlowDelete,
  ApprovalFlowDeleteVariables,
} from '../__generated__/ApprovalFlowDelete';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'absolute',
    width: '100%',
    backgroundColor: theme.palette.neutralLighterAlt,
    zIndex: 99,
    cursor: 'move',
  },

  approvalTypeFont: {
    color: theme.palette.themePrimary,
    transition: '0.3s ease',
    ':hover': {
      transform: 'translateX(2px)',
      color: theme.palette.themeDarker,
    },
  },
}));

type HeaderProps = {
  onClose: () => void;
  isNew: boolean;
  approvalDetailsData: FlowApproval | undefined;
  approvalDetailsLoading: boolean;
  approvalTypes: ApprovalSetupFlowCommonData_Query | undefined;
  firstRowPriorityNumber: number;
  isTestingView:boolean;
};

export const Header: React.FC<HeaderProps> = ({
  onClose,
  approvalDetailsData,
  approvalDetailsLoading: loading,
  isNew,
  approvalTypes,
  firstRowPriorityNumber,
  isTestingView
}) => {
  const styles = useStyles();
  const history = useHistory();
  const commonStyles = useCommonStyles();
  const expanded = useReactiveVar(expandedNav);
  const {
    formState: { isDirty, isSubmitting },
  } = useFormContext();

  const [deleteApproval] = useMutation<
    ApprovalFlowDelete,
    ApprovalFlowDeleteVariables
  >(DELETE_APPROVAL, { errorPolicy: 'all' });
  const _deleteTransaction = async () => {
    const { errors } = await deleteApproval({
      variables: {
        input: {
          entityDelete: [
            {
              id: approvalDetailsData?.approval?.id!,
              rowTimestamp: approvalDetailsData?.approval?._rowTimestamp!,
            },
          ],
        },
      },
      update: (cache, { data, errors }) => {
        if (!errors?.length) {
          setTimeout(() => {
            const identity = cache.identify({
              ...approvalDetailsData?.approval,
            });
            cache.evict({ id: identity });
            cache.gc();
          }, 200);

          cache.modify({
            fields: {
              approvals(existing) {
                return {
                  ...existing,
                  totalCount: existing.totalCount - 1,
                } as Approvals_approvals;
              },
            },
          });
        }
      },
    });
    if (errors?.length)
      showCustomToast({
        content: `${errors[0].message}`,
        appearance: 'error',
      });
    else {
      showCustomToast({
        content: 'Record deleted successfully',
        appearance: 'success',
      });
      history.push('/account-management/approval-setup');
    }
  };

  return (
    <Stack
      horizontal
      className={styles.container}
      horizontalAlign="space-between"
      verticalAlign="center"
      tokens={{
        padding: 20,
      }}
      style={{
        width: `calc(100vw - ${
          expanded ? DRAWER_OPEN_WIDTH : DRAWER_CLOSE_WIDTH
        }px)`,
      }}
    >
      {/* START */}
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <Text variant="xLarge" className={commonStyles.bold}>
            Approval Studio
          </Text>
        </Stack>

        {loading || isNew || (
          <Stack>
            <Text variant="xLarge" className={clsx(styles.approvalTypeFont)}>
              - {approvalDetailsData?.approval?.approvalType?.approvalType}
            </Text>
          </Stack>
        )}
      </Stack>

      {/* MIDDLE */}
      <Stack verticalAlign="center">
        {loading || (
          <Text variant="xLarge">
            {approvalDetailsData?.approval?._fullApprovalDescription}
          </Text>
        )}
      </Stack>

      {/* END */}
      <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="center">
        <UnsavedIndicator
          visible={!isNew && !loading && isDirty && !isSubmitting}
        />
        <ApprovalFlowModal
          entityId={approvalDetailsData?.approval?.id!}
          customButton={
            <Stack>
              <TooltipHost content={'Approval flow'}>
                <IconButton iconProps={{ iconName: 'Dataflows' }} />
              </TooltipHost>
            </Stack>
          }
        />

        {loading || isNew || (
          <Stack
            tokens={{ childrenGap: 10, padding: '0px 20px' }}
            verticalAlign="center"
            horizontal
            horizontalAlign="space-between"
          >
            <StatusIcon
              approval={false}
              iconType={approvalDetailsData?.approval?.statusType!}
            />
            <Stack>
              <Text>
                {approvalDetailsData?.approval?.statusType?.statusType}
              </Text>
            </Stack>
          </Stack>
        )}

        {!isNew && (
          <ActionMessageModal
            entityType={EntityType.ApprovalConfig}
            disabled={!approvalDetailsData?.approval?._isDeletable || isTestingView}
            onConfirm={() => {
              _deleteTransaction();
            }}
          />
        )}
        <CopyApproval
          id={approvalDetailsData?.approval?.id!}
          data={approvalTypes?.approvalTypes!}
          firstRowPriorityNumber={firstRowPriorityNumber}
        />
        <ActionsMenu approvalDetails={approvalDetailsData!} />
        <CloseButton onClick={onClose} />
      </Stack>
    </Stack>
  );
};
